/**
 * How the assistant panel and the page it photographs talk.
 *
 * The panel loads the page the model asked for in a hidden, same-origin frame
 * with two query parameters. The page (app.tsx) then renders without its chrome,
 * photographs the element and posts the result back to the panel. See
 * core/logic/assistant/show_view.py for the server half.
 */
import type { PendingView, ViewMode } from "../../api/assistant/assistant";

/** The element to photograph; empty for the whole page. */
export const VIEW_FOCUS_PARAM = "assistant_view";
/** The model's tool call this picture answers. Its presence means "embedded". */
export const VIEW_TOKEN_PARAM = "assistant_view_token";
/** "outline" or "picture" (ViewMode). */
export const VIEW_MODE_PARAM = "assistant_view_mode";
/** The most characters the outline may have; the capture cuts it there. */
export const VIEW_OUTLINE_MAX_PARAM = "assistant_view_outline_max";

export const isViewMode = (value: string | null): value is ViewMode =>
	value === "outline" || value === "picture";

/** Same pattern the server enforces (FOCUS_RE): data-* attribute selectors only. */
export const FOCUS_PATTERN = /^(\[data-[a-z0-9-]+(="[A-Za-z0-9_-]+")?\])*$/;

export const VIEW_MESSAGE_TYPE = "assistant-view";

export interface ViewCaptureMessage {
	type: typeof VIEW_MESSAGE_TYPE;
	token: string;
	found: boolean;
	/** The element's visible text, or why it could not be photographed. */
	text: string;
	/** PNG as base64, without the data: prefix; empty unless mode picture. */
	png: string;
	/** The page as indented text; empty unless mode outline. */
	outline: string;
	outline_truncated: boolean;
}

export const viewEmbedUrl = (view: PendingView): string => {
	const params = new URLSearchParams({
		[VIEW_FOCUS_PARAM]: view.focus,
		[VIEW_TOKEN_PARAM]: view.tool_use_id,
		[VIEW_MODE_PARAM]: view.mode,
		[VIEW_OUTLINE_MAX_PARAM]: String(view.outline_max_chars),
	});
	const separator = view.route.includes("?") ? "&" : "?";
	return `${view.route}${separator}${params.toString()}`;
};
